import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
	calcMonthlyMembershipTierAmount,
	ChallengeDto,
	CompactTicket,
	CreateEventTicketDto,
	EventPriceDto,
	GetPricesAndFeesDto,
	getShortPeriodFromFrequency,
	MembershipDto,
	MembershipTierFrequencyEnum,
	priceFormatter,
	BlastPlanDto,
	EventBundleDto, DEFAULT_CURRENCY,
} from '@sweatpals/shared';
import { format } from 'date-fns';
import { orderSummaryStyles } from './order-summary.styles';
import { CoreTooltip } from '../_core/core-tooltip';
import { Showable } from '../utils';
import { clsx, useStyles } from '../../utils';

export interface OrderSummaryProps {
	prices?: EventPriceDto[];
	membership?: MembershipDto;
	challenge?: ChallengeDto;
	eventBundle?: EventBundleDto;
	blastPlan?: BlastPlanDto;
	selectedMembershipTierId?: string;
	tickets?: CreateEventTicketDto[];
	upgradedTicket?: {
		eventPriceId: string;
		tierName: string;
		priceAmount: number;
	};
	priceAndFees?: GetPricesAndFeesDto;
	promocode?: string;
	isPromocodeApplied?: boolean;
	isDonationBased?: boolean;
}

export const OrderSummary: FC<OrderSummaryProps> = ({
	tickets,
	membership,
	selectedMembershipTierId,
	prices,
	priceAndFees,
	promocode,
	isPromocodeApplied,
	isDonationBased,
	upgradedTicket,
	challenge,
	blastPlan,
	eventBundle,
}) => {
	const [isMembershipNameOverflowed, setIsMembershipNameOverflowed] = useState(false);
	const membershipNameElementRef = useRef<HTMLDivElement | null>(null);
	const currency = priceAndFees?.currency || DEFAULT_CURRENCY;

	useEffect(() => {
		if (membershipNameElementRef.current) {
			const { scrollWidth, clientWidth } = membershipNameElementRef.current;
			setIsMembershipNameOverflowed(scrollWidth > clientWidth);
		}
	}, []);

	const ticketsWithPrice = useMemo(
		() =>
			tickets?.map(ticket => ({
				...ticket,
				eventPrice: prices?.find(price => price.id === ticket.eventPriceId),
			})),
		[prices, tickets]
	);

	const compactTickets = ticketsWithPrice?.map(ticket => ({
		...ticket,
		pricePerTicket: ticket.eventPrice?.priceAmount,
		sumPrice: (ticket.eventPrice?.priceAmount ?? 0) * ticket.quantity,
	})) as CompactTicket[];

	const selectedMembershipTier = useMemo(
		() => membership?.tiers?.find(tier => tier.id === selectedMembershipTierId),
		[membership?.tiers, selectedMembershipTierId]
	);

	const styles = useStyles(orderSummaryStyles);

	return (
		<Box display='flex' flexDirection='column' gap='18px'>
			<Box display='flex' flexDirection='column' gap='12px'>
				<Showable show={selectedMembershipTier}>
					{() => (
						<Box
							sx={clsx(styles.grid, {
								gridTemplateColumns: selectedMembershipTier?.frequency === MembershipTierFrequencyEnum.OneTime
									? '4.6fr 1.4fr'
									: '4fr auto 1.4fr',
							})}
						>
							<Box display='flex' flexDirection='column' gap='4px' overflow='hidden'>
								<CoreTooltip
									arrow
									placement='top'
									title={membership?.name}
									disableHoverListener={!isMembershipNameOverflowed}
									disableTouchListener={!isMembershipNameOverflowed}
								>
									<Typography sx={styles.membershipName} ref={membershipNameElementRef}>
										{membership?.name}
									</Typography>
								</CoreTooltip>
								<Showable hide={selectedMembershipTier?.frequency === MembershipTierFrequencyEnum.OneTime}>
									<Typography sx={styles.membershipMonthlyPrice}>
										{priceFormatter(calcMonthlyMembershipTierAmount(selectedMembershipTier), { currency })} / month
									</Typography>
								</Showable>
								<Showable show={!!selectedMembershipTier?.startDate && !!selectedMembershipTier?.endDate}>
									{() =>
										selectedMembershipTier?.startDate && selectedMembershipTier?.endDate && (
											<Typography sx={styles.membershipPeriod}>
												{format(new Date(selectedMembershipTier?.startDate), 'd MMM yyyy')}
												{' - '}
												{format(new Date(selectedMembershipTier?.endDate), 'd MMM yyyy')}
											</Typography>
										)
									}
								</Showable>
							</Box>
							<Showable hide={selectedMembershipTier?.frequency === MembershipTierFrequencyEnum.OneTime}>
								<Typography sx={styles.tierFrequency}>
									{getShortPeriodFromFrequency(selectedMembershipTier?.frequency)}
								</Typography>
							</Showable>
							<Typography sx={styles.price}>
								{priceFormatter(selectedMembershipTier?.amount, { currency })}
							</Typography>
						</Box>
					)}
				</Showable>

				<Showable show={isDonationBased}>
					{() => (
						<Box sx={styles.grid}>
							<Typography sx={styles.tierName}>Donation</Typography>
							<Box />
							<Typography sx={styles.price}>{priceFormatter(priceAndFees?.basePrice ?? 0, { currency })}</Typography>
						</Box>
					)}
				</Showable>

				<Showable show={compactTickets?.length}>
					{() => (
						compactTickets?.map(ticket => (
							<Box key={ticket.eventPriceId} sx={styles.grid}>
								<Typography sx={styles.tierName}>{ticket.eventPrice?.tierName}</Typography>
								<Typography sx={styles.tierQuantity}>x{ticket.quantity}</Typography>
								<Typography sx={styles.price}>
									{priceFormatter(ticket.sumPrice ?? 0, { currency })}
								</Typography>
							</Box>
						))
					)}
				</Showable>

				<Showable show={upgradedTicket}>
					{() => (
						<Box sx={styles.grid}>
							<Typography sx={styles.tierName}>{upgradedTicket?.tierName}</Typography>
							<Box />
							<Typography sx={styles.price}>{priceFormatter(upgradedTicket?.priceAmount, { currency })}</Typography>
						</Box>
					)}
				</Showable>

				<Showable show={challenge}>
					{() => (
						<Box sx={styles.grid}>
							<Typography sx={styles.tierName}>Challenger Pass</Typography>
							<Typography sx={styles.tierQuantity}>x1</Typography>
							<Typography sx={styles.price}>{priceFormatter(challenge?.amount, { currency })}</Typography>
						</Box>
					)}
				</Showable>

				<Showable show={blastPlan}>
					{() => (
						<Box sx={styles.grid}>
							<Typography sx={styles.tierName}>{blastPlan?.type === 'addon' ? 'Advanced batch' : 'Monthly'}</Typography>
							<Typography sx={styles.tierQuantity}>x1</Typography>
							<Typography sx={styles.price}>{priceFormatter(blastPlan?.price, { currency })}</Typography>
						</Box>
					)}
				</Showable>

				<Showable show={eventBundle}>
					{() => (
						<Box sx={styles.grid}>
							<Typography sx={styles.tierName}>{eventBundle?.name}</Typography>
							<Typography sx={styles.tierQuantity}>x1</Typography>
							<Typography sx={styles.price}>{priceFormatter(eventBundle?.price ?? 0, { currency })}</Typography>
						</Box>
					)}
				</Showable>

				<Showable show={selectedMembershipTier?.signUpFee}>
					{() => (
						<Box sx={styles.grid}>
							<Typography sx={styles.tierName}>
								Initiation fee
							</Typography>
							<Box />
							<Typography sx={{ ...styles.price, minWidth: 'unset' }}>
								{priceFormatter(selectedMembershipTier?.signUpFee ?? 0, { currency })}
							</Typography>
						</Box>
					)}
				</Showable>

				<Box sx={styles.grid}>
					<Typography sx={styles.tierName}>
						Service Fee
					</Typography>
					<Box />
					<Typography sx={{ ...styles.price, minWidth: 'unset' }}>
						{priceFormatter(priceAndFees?.sweatPalsFee ?? 0, { currency })}
					</Typography>
				</Box>

				<Box sx={styles.grid}>
					<Typography sx={styles.tierName}>
						Payment Processing Fee
					</Typography>
					<Box />
					<Typography sx={{ ...styles.price, minWidth: 'unset' }}>
						{priceFormatter(priceAndFees?.stripeFee ?? 0, { currency })}
					</Typography>
				</Box>

				<Showable show={isPromocodeApplied}>
					{() => (
						<Box sx={styles.grid}>
							<Typography sx={{ ...styles.tierName, textTransform: 'uppercase' }}>{promocode}</Typography>
							<Box />
							<Typography sx={styles.price}>
								- {priceFormatter((priceAndFees?.discountAmount ?? 0) - (priceAndFees?.membershipDiscountAmount ?? 0), { currency })}
							</Typography>
						</Box>
					)}
				</Showable>

				<Showable show={priceAndFees?.membershipDiscountAmount}>
					{() => (
						<Box sx={styles.grid}>
							<Typography sx={styles.tierName}>Membership Discount</Typography>
							<Box />
							<Typography sx={{ ...styles.price, minWidth: 'unset' }}>
								- {priceFormatter(priceAndFees?.membershipDiscountAmount ?? 0, { currency })}
							</Typography>
						</Box>
					)}
				</Showable>

				<Showable show={priceAndFees?.eventBundleDiscountAmount}>
					{() => (
						<Box sx={styles.grid}>
							<Typography sx={styles.tierName}>Event Bundle Discount</Typography>
							<Box />
							<Typography sx={{ ...styles.price, minWidth: 'unset' }}>
								- {priceFormatter(priceAndFees?.eventBundleDiscountAmount ?? 0, { currency })}
							</Typography>
						</Box>
					)}
				</Showable>

				<Showable show={priceAndFees?.trackingLinkDiscountAmount}>
					{() => (
						<Box sx={styles.grid}>
							<Typography sx={styles.tierName}>Referral discount</Typography>
							<Box />
							<Typography sx={{ ...styles.price, minWidth: 'unset' }}>
								- {priceFormatter(priceAndFees?.trackingLinkDiscountAmount ?? 0, { currency })}
							</Typography>
						</Box>
					)}
				</Showable>
			</Box>

			<Box sx={{ borderBottom: `1px solid #FFFFFF1A` }} />

			<Box sx={styles.grid}>
				<Typography sx={styles.textBigger}>Total</Typography>
				<Box />
				<Typography id="total-price" sx={styles.totalPrice}>
					{priceFormatter(priceAndFees?.fullPrice ?? 0, { currency })}
				</Typography>
			</Box>
		</Box>
	);
};
